import { GoogleGenerativeAI } from '@google/generative-ai';
import OpenAI from 'openai';
import { v4 as uuidv4 } from 'uuid';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

/**
 * Exam-specific configuration for AI question generation
 */
const EXAM_CONFIGS = {
  mcat: {
    name: 'MCAT (Medical College Admission Test)',
    role: 'expert MCAT question writer with deep knowledge of the AAMC exam blueprint and extensive experience writing questions that mirror the style, rigor, and cognitive demands of the real MCAT',
    systemMessage: 'You are an expert MCAT question writer who has written thousands of MCAT-style questions used in professional test prep. You write questions that match real MCAT exam style, difficulty, and reasoning depth. Every question must be indistinguishable from a real AAMC practice question. You only respond with valid JSON.',
    questionStyle: `
QUESTION WRITING RULES (MCAT):
- Write questions that require multi-step reasoning and integration of concepts across disciplines — pure recall questions are NOT acceptable
- Model the style of AAMC MCAT Section Bank and official practice materials
- Question stems should present a scenario, experimental setup, research finding, or data interpretation challenge
- Use question formats like: "What would happen if...", "Which finding best supports...", "Based on the data, which conclusion...", "A researcher observes X. Which of the following best explains..."
- Use precise scientific terminology at the level expected of undergraduate pre-medical students
- For calculation-based questions, require 2-3 logical steps — never just plug-and-chug
- Questions should test APPLICATION and ANALYSIS of knowledge, not just recognition
- Include questions that require distinguishing between superficially similar concepts
- For behavioral sciences, include experimental design interpretation and statistical reasoning
- NEVER explain abbreviations or acronyms in parentheses — use them directly (e.g., write "ATP" not "ATP (adenosine triphosphate)")
- Difficulty distribution: 30% foundational application, 45% intermediate analysis, 25% challenging synthesis`,
    distractorGuidance: `
DISTRACTOR (WRONG ANSWER) DESIGN RULES (MCAT):
- Every wrong answer MUST be plausible to a student who holds a specific misconception or makes a common reasoning error
- Distractor strategies to use:
  (a) Correct concept applied to the wrong context or condition
  (b) Partially correct reasoning that misses a critical step or variable
  (c) Common arithmetic, sign, or unit conversion errors for quantitative questions
  (d) Confusing related but distinct concepts (e.g., competitive vs. uncompetitive inhibition, sympathetic vs. parasympathetic effects)
  (e) True statements that do not actually answer the question asked
  (f) Answers that would be correct if a different variable were changed
- NEVER include obviously wrong, absurd, or joke answers
- All distractors must be similar in length, specificity, and grammatical structure to the correct answer
- Randomize which position (A, B, C, D) holds the correct answer — do NOT default to A`,
    explanationGuidance: `
Explanation format (write all of this in a single text block).
Use **double asterisks** around text that should be bold — for section headers and important terms/concepts.

**The correct answer is [Letter].**
[Provide the complete chain of reasoning that leads to this answer. Start from the relevant principle or concept, walk through the logical steps, reference specific details from the question stem, and arrive at the conclusion. Bold any **key terms**, **enzyme names**, **pathway names**, or **critical concepts** that are central to the explanation. This should read like a mini-lecture that teaches the student HOW to think through this type of problem.]

**Why [Letter] is incorrect:**
[For each wrong answer, explain: (1) what misconception or error leads a student to pick this, (2) why the reasoning breaks down, and (3) what the correct understanding should be. Be specific — don't just say "this is wrong because the correct answer is X."]

**Why [Letter] is incorrect:**
[Repeat for each wrong answer]

**Why [Letter] is incorrect:**
[Repeat for each wrong answer]

**Key Concept:**
[Provide a 3-5 sentence educational summary that goes BEYOND just this question. Expand on the underlying topic, mention related high-yield concepts, explain how this topic commonly appears on the MCAT, and give the student a broader framework for understanding. Bold any **key terms** or **high-yield facts**. This section should make the student smarter about the entire topic, not just this one question.]`,
    passageStyle: `Generate a passage that is INDISTINGUISHABLE from a real MCAT passage:
- 4-6 paragraphs of dense, college-level scientific writing
- Present a real-seeming research study with hypothesis, experimental methodology, specific results (include numbers, percentages, p-values where relevant), and conclusions
- Write in formal academic journal style with discipline-appropriate terminology
- Include at least one data point, finding, or observation that questions can specifically reference
- The passage should require careful, analytical reading — not just skimming
- Include enough complexity that questions can test different levels of comprehension (literal, inferential, and evaluative)
- Topics should reflect actual MCAT content areas: biochemistry, biology, chemistry, physics, psychology, sociology`,
    difficultyDescription: 'foundational application (30%), intermediate analysis (45%), challenging synthesis (25%)'
  },

  dat: {
    name: 'DAT (Dental Admission Test)',
    role: 'expert DAT question writer with deep knowledge of the ADA DAT exam blueprint and extensive experience writing questions that mirror the style, content scope, and pacing of the real DAT',
    systemMessage: 'You are an expert DAT question writer who has written thousands of DAT-style questions used in professional dental test prep. You write questions that match real DAT exam style, difficulty, and content focus. Every question must be indistinguishable from a real DAT practice question. You only respond with valid JSON.',
    questionStyle: `
QUESTION WRITING RULES (DAT):
- Write questions that are concise and direct — the DAT has a faster pace than the MCAT (~1 minute per question), so stems should be focused
- Cover DAT natural science content: biology (cell biology, genetics, anatomy, physiology, developmental biology, ecology, evolution), general chemistry (stoichiometry, thermodynamics, kinetics, acids/bases, electrochemistry, nuclear chemistry), and organic chemistry (reactions, mechanisms, functional groups, stereochemistry, spectroscopy)
- Include dental-relevant topics when appropriate: tooth development and morphology, craniofacial anatomy, microbiology of oral pathogens, salivary gland function, bone remodeling, TMJ anatomy
- DAT questions focus on clear, single-concept or two-step reasoning — fewer multi-step synthesis problems than MCAT
- Question stems should be 1-3 sentences for standalone questions
- Include a mix of: direct knowledge application, "which of the following" identification, "what would happen if" prediction, and process-ordering questions
- For organic chemistry, emphasize reaction prediction, reagent identification, and mechanism-based reasoning
- NEVER explain abbreviations or acronyms in parentheses — use them directly (e.g., write "DNA" not "DNA (deoxyribonucleic acid)")
- Difficulty distribution: 25% straightforward recall with application, 50% application and analysis, 25% challenging reasoning`,
    distractorGuidance: `
DISTRACTOR (WRONG ANSWER) DESIGN RULES (DAT):
- Every wrong answer MUST target a specific knowledge gap or common student error
- Distractor strategies to use:
  (a) Confusing similar biological structures, pathways, or processes (e.g., mitosis vs. meiosis stages, different enzyme families)
  (b) Mixing up properties of related chemical compounds or functional groups
  (c) Choosing the right concept but wrong specific detail (e.g., correct organelle but wrong function)
  (d) Common memorization mix-ups (e.g., cranial nerve numbers, amino acid properties)
  (e) Reversed relationships (e.g., direct vs. inverse proportionality)
  (f) Off-by-one errors in reaction steps or biological sequences
- NEVER use "all of the above" or "none of the above"
- All distractors must be concise and parallel in structure to the correct answer
- Randomize which position holds the correct answer`,
    explanationGuidance: `
Explanation format (write all of this in a single text block).
Use **double asterisks** around text that should be bold — for section headers and important terms/concepts.

**The correct answer is [Letter].**
[Provide a clear, direct explanation of why this is correct. State the relevant concept or rule, explain how it applies to this specific question, and walk through the reasoning. Bold any **key terms**, **structures**, **reactions**, or **critical facts** that are central to the explanation. Be concise but thorough — DAT students need to review efficiently.]

**Why [Letter] is incorrect:**
[Explain what specific misconception or knowledge gap leads to this answer. State what this answer confuses or gets wrong, and clarify the correct understanding.]

**Why [Letter] is incorrect:**
[Repeat for each wrong answer]

**Why [Letter] is incorrect:**
[Repeat for each wrong answer]

**Key Concept:**
[Provide a 3-5 sentence review of the fundamental concept being tested. Include high-yield DAT facts, related concepts that are commonly tested together, and any mnemonics or memory aids that help. Bold any **key terms** or **high-yield facts**. This should serve as a mini-review that reinforces the student's understanding of the entire topic area.]`,
    passageStyle: `Generate a passage in the style of DAT reading comprehension or Survey of Natural Sciences passages:
- 2-3 paragraphs of clear, focused scientific writing
- Shorter and more focused than MCAT passages — get to the point
- Present a scientific concept, biological process, chemical principle, or dental-relevant topic with enough detail to support questions
- Use straightforward academic language appropriate for undergraduate science students
- Include specific facts, processes, or data that questions can reference directly
- Focus on natural sciences relevant to dental education and the DAT blueprint`,
    difficultyDescription: 'recall with application (25%), application and analysis (50%), challenging reasoning (25%)'
  },

  pmr: {
    name: 'PM&R (Physical Medicine & Rehabilitation Board Exam)',
    role: 'expert PM&R board exam question writer with deep clinical knowledge of physical medicine and rehabilitation, and extensive experience writing board-style questions that mirror the ABPMR certification exam',
    systemMessage: 'You are an expert PM&R board exam question writer who has written thousands of board-style clinical vignette questions used in PM&R residency training and board review. You write questions that match real ABPMR certification exam style and clinical complexity. Every question must be indistinguishable from a real PM&R board question. You only respond with valid JSON.',
    questionStyle: `
QUESTION WRITING RULES (PM&R Board Exam):
- Write questions as CLINICAL VIGNETTES: present a patient case with relevant demographics, chief complaint, history of present illness, pertinent past medical/surgical history, physical examination findings, and any relevant diagnostic results (imaging, electrodiagnostics, labs)
- Cover PM&R core content areas: musculoskeletal medicine (spine, joints, sports medicine), neurological rehabilitation (stroke, TBI, SCI), electrodiagnostic medicine (NCS/EMG interpretation), prosthetics and orthotics, pediatric rehabilitation (cerebral palsy, spina bifida), pain management, cancer rehabilitation, cardiac/pulmonary rehabilitation, and functional assessment
- Question types should include: "What is the most likely diagnosis?", "What is the best next step in management?", "Which finding on electrodiagnostic testing would you expect?", "What is the most appropriate rehabilitation intervention?", "What is the expected functional outcome?"
- Use realistic clinical scenarios with specific details: age, sex, mechanism of injury, timeline of symptoms, specific examination maneuvers and their results (e.g., "positive Spurling's test", "4/5 strength in wrist extensors")
- Include questions that require distinguishing between clinically similar presentations (e.g., C6 vs. C7 radiculopathy, UMN vs. LMN findings, different peripheral nerve injuries)
- NEVER explain abbreviations or acronyms in parentheses — use them directly (e.g., write "SCI" not "SCI (spinal cord injury)", write "TBI" not "TBI (traumatic brain injury)"). The student is expected to know medical abbreviations.
- Difficulty distribution: 20% foundational clinical knowledge, 50% clinical application and management decisions, 30% advanced clinical reasoning and complex cases`,
    distractorGuidance: `
DISTRACTOR (WRONG ANSWER) DESIGN RULES (PM&R Board):
- Every wrong answer should represent a plausible clinical decision that a less experienced clinician or resident might choose
- Distractor strategies to use:
  (a) Correct treatment but wrong timing, dose, or indication (e.g., botulinum toxin injection when oral medication should be tried first)
  (b) Appropriate management for a similar but different diagnosis (e.g., treating C7 radiculopathy management when the presentation is C6)
  (c) Outdated or less evidence-based management approaches
  (d) Confusing related conditions with overlapping presentations (e.g., lateral epicondylitis vs. radial tunnel syndrome)
  (e) Correct diagnostic finding for a different condition or nerve level
  (f) Interventions that are contraindicated given specific patient factors in the vignette
- All distractors must be realistic clinical options — no obviously absurd choices
- Distractors should match the specificity and clinical detail level of the correct answer
- Randomize which position holds the correct answer`,
    explanationGuidance: `
Explanation format (write all of this in a single text block).
Use **double asterisks** around text that should be bold — for section headers and important terms/concepts.

**The correct answer is [Letter].**
[Provide the complete clinical reasoning: explain the relevant pathophysiology, how the clinical presentation in the vignette points to this answer, what examination findings or diagnostic results are key discriminators, and why this management approach is evidence-based or guideline-supported. Bold any **diagnoses**, **exam findings**, **nerve levels**, **medications**, or **clinical guidelines** that are central to the explanation. Reference specific clinical features from the vignette.]

**Why [Letter] is incorrect:**
[Explain what clinical scenario this answer WOULD be appropriate for, and specifically why it does not fit THIS patient's presentation. Identify what a student confusing this answer would be misunderstanding about the clinical picture.]

**Why [Letter] is incorrect:**
[Repeat for each wrong answer]

**Why [Letter] is incorrect:**
[Repeat for each wrong answer]

**Key Concept:**
[Provide a 3-5 sentence clinical pearl about the underlying condition, rehabilitation principle, or management guideline. Include: the classic presentation, key physical exam findings, important diagnostic criteria, evidence-based management approach, and expected functional outcomes. Bold any **key clinical terms** or **high-yield facts**. This should serve as a board-review-quality summary that expands the learner's clinical knowledge beyond just this question.]`,
    passageStyle: `Generate a detailed clinical vignette passage in the style of PM&R board exam cases:
- 3-5 paragraphs presenting a comprehensive patient case
- Paragraph 1: Demographics, chief complaint, mechanism of injury or onset of symptoms
- Paragraph 2: History of present illness with timeline, aggravating/alleviating factors, associated symptoms
- Paragraph 3: Physical examination findings with specific test results, functional assessment
- Paragraph 4 (if applicable): Diagnostic study results (imaging, electrodiagnostics, labs)
- Use standard medical terminology and documentation style
- Include enough clinical detail to support multiple questions about diagnosis, management, prognosis, and rehabilitation planning
- The case should be medically realistic and reflect actual PM&R clinical practice`,
    difficultyDescription: 'foundational clinical knowledge (20%), clinical application (50%), advanced clinical reasoning (30%)'
  }
};

/**
 * Get exam configuration, defaulting to MCAT for backward compatibility
 */
const getExamConfig = (examType) => {
  return EXAM_CONFIGS[examType] || EXAM_CONFIGS.mcat;
};

/**
 * Build the prompt for AI question generation
 */
const buildPrompt = (params, existingPassageText = null) => {
  const {
    subjectName,
    unitName,
    topicName,
    count,
    numberOfChoices,
    additionalContext,
    isPassageBased,
    passageMode,
    examType
  } = params;

  const config = getExamConfig(examType);
  const choiceLabels = Array.from({ length: numberOfChoices }, (_, i) => String.fromCharCode(65 + i));

  let prompt = `You are an ${config.role}.

Your task: Generate ${count} high-quality multiple-choice questions that are INDISTINGUISHABLE from real exam questions.

===== EXAM CONTEXT =====
Exam: ${config.name}
Subject: ${subjectName}
Unit/Discipline: ${unitName}${topicName ? `\nTopic: ${topicName}` : ''}
Number of choices per question: ${numberOfChoices} (${choiceLabels.join(', ')})
Target difficulty distribution: ${config.difficultyDescription}
`;

  if (additionalContext) {
    prompt += `\nAdditional context/requirements: ${additionalContext}\n`;
  }

  // Passage handling
  if (isPassageBased && passageMode === 'existing' && existingPassageText) {
    prompt += `\nThe questions must be based on the following passage:\n"""${existingPassageText}"""\n`;
  } else if (isPassageBased && passageMode === 'new') {
    prompt += `\n===== PASSAGE GENERATION =====\n${config.passageStyle}\n`;
  }

  // Question style guidance
  prompt += `
===== QUESTION WRITING GUIDELINES =====
${config.questionStyle}

===== DISTRACTOR (WRONG ANSWER) GUIDELINES =====
${config.distractorGuidance}

===== ANSWER EXPLANATION GUIDELINES =====
For each question, you MUST provide a comprehensive, educational explanation in a SINGLE text string following this exact internal structure:
${config.explanationGuidance}

===== RESPONSE FORMAT =====
CRITICAL: You must respond with ONLY valid JSON. No markdown, no code fences, no explanation text outside the JSON structure.

Return a JSON object with this exact structure:
{
  ${isPassageBased && passageMode === 'new' ? `"passage": {
    "title": "A descriptive title for the passage",
    "text": "The full passage text"
  },` : ''}
  "questions": [
    {
      "questionText": "The full question text (for passage-based questions, this should reference the passage but be answerable when reading both together)",
      "choices": [${choiceLabels.map((label, i) => `
        { "id": "${i}", "label": "${label}", "text": "Choice text" }`).join(',')}
      ],
      "correctAnswerId": "The id string of the correct choice (e.g., '0', '1', '2', etc.)",
      "explanationText": "Complete explanation using **double asterisks** for bold on section headers and key terms. Format: '**The correct answer is [X].** ...' then '**Why [X] is incorrect:** ...' for each wrong answer, then '**Key Concept:** ...' — all in one string",
      "difficulty": "easy|medium|hard",
      "quality": "simple|excellent"
    }
  ]
}

===== FINAL QUALITY REQUIREMENTS =====
- Each question MUST be unique and test a different concept, angle, or cognitive skill within the subject area
- Questions MUST be at authentic ${config.name} difficulty following: ${config.difficultyDescription}
- Explanations MUST follow the structured format: "**The correct answer is [X].**" reasoning, "**Why [X] is incorrect:**" for each wrong answer, "**Key Concept:**" expansion — all within the single explanationText string
- Use **double asterisks** to bold section headers and important terms/concepts throughout the explanation (e.g., **key terms**, **enzyme names**, **diagnoses**)
- NEVER explain abbreviations or acronyms in parentheses. Do NOT write things like "Ser189 (where serine at position 189...)" or "SCI (spinal cord injury)" or "TBI (traumatic brain injury)". The student is expected to know what abbreviations and acronyms mean — spelling them out defeats the purpose of testing that knowledge. Use abbreviations and medical/scientific terminology directly without parenthetical definitions.
- Each explanation should be thorough enough to serve as a learning resource — a student who reads only the explanation should understand the entire topic
- Only ONE correct answer per question
- correctAnswerId must be the "id" string of the correct choice (e.g., "0", "1", "2", etc.)
- VARY the correct answer position across questions — do NOT always put the correct answer in the same position
- Set quality to "excellent" for multi-step reasoning or synthesis questions, "simple" for straightforward application questions
- Do NOT include any text outside the JSON object
`;

  return prompt;
};

/**
 * Parse a line of text for **bold** markers and return plain text + BOLD inlineStyleRanges
 */
const parseBoldMarkers = (line) => {
  const ranges = [];
  let plainText = '';
  let i = 0;

  while (i < line.length) {
    if (line[i] === '*' && line[i + 1] === '*') {
      // Find closing **
      const closeIdx = line.indexOf('**', i + 2);
      if (closeIdx !== -1) {
        const boldContent = line.substring(i + 2, closeIdx);
        ranges.push({
          offset: plainText.length,
          length: boldContent.length,
          style: 'BOLD'
        });
        plainText += boldContent;
        i = closeIdx + 2;
      } else {
        // No closing **, treat as literal
        plainText += line[i];
        i++;
      }
    } else {
      plainText += line[i];
      i++;
    }
  }

  return { text: plainText, inlineStyleRanges: ranges };
};

/**
 * Convert plain text to Draft.js raw content state format
 * Supports **bold** markers which are converted to Draft.js BOLD inlineStyleRanges
 */
const textToDraftRaw = (text) => {
  if (!text) {
    return {
      blocks: [{
        key: uuidv4().substring(0, 5),
        text: '',
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {}
      }],
      entityMap: {}
    };
  }

  // Split text into paragraphs for multi-paragraph content
  const paragraphs = text.split('\n').filter(p => p.trim() !== '');

  return {
    blocks: paragraphs.map(paragraph => {
      const { text: plainText, inlineStyleRanges } = parseBoldMarkers(paragraph);
      return {
        key: uuidv4().substring(0, 5),
        text: plainText,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges,
        entityRanges: [],
        data: {}
      };
    }),
    entityMap: {}
  };
};

/**
 * Parse the AI JSON response into the app's data structure
 */
const parseAIResponse = (jsonData, params, existingPassage) => {
  const result = {
    questions: [],
    passage: null
  };

  // Handle passage
  if (existingPassage) {
    result.passage = existingPassage;
  } else if (params.isPassageBased && params.passageMode === 'new' && jsonData.passage) {
    result.passage = {
      tempPassageId: uuidv4(),
      title: jsonData.passage.title || `AI Generated Passage: ${params.topicName || params.unitName}`,
      passageText: textToDraftRaw(jsonData.passage.text),
      subjectId: params.subjectId,
      unitId: params.unitId,
      tags: [],
      // Include figure if generated
      ...(params.passageFigureUrl ? {
        figureUrl: params.passageFigureUrl,
        figureDescription: params.passageFigureDescription || ''
      } : {})
    };
  }

  // Parse questions
  const questions = jsonData.questions || [];
  for (const q of questions) {
    const choices = (q.choices || []).map((choice, index) => ({
      choiceId: index.toString(),
      text: textToDraftRaw(choice.text)
    }));

    const question = {
      questionText: textToDraftRaw(q.questionText),
      choices: choices,
      correctAnswer: [q.correctAnswerId?.toString() || '0'],
      explanationText: textToDraftRaw(q.explanationText),
      lessonReference: textToDraftRaw(''),
      difficulty: q.difficulty || 'medium',
      quality: q.quality || 'simple',
      choiceType: 'multiple_choice'
    };

    // Link to passage if applicable
    if (result.passage) {
      question.isPassageBased = true;
      if (result.passage.passageId) {
        question.passageId = result.passage.passageId;
      } else {
        question.tempPassageId = result.passage.tempPassageId;
      }
    }

    result.questions.push(question);
  }

  return result;
};

/**
 * Extract JSON from AI response text (handles markdown code fences)
 */
const extractJSON = (text) => {
  // Try parsing directly first
  try {
    return JSON.parse(text);
  } catch (e) {
    // Try extracting from markdown code fences
    const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[1].trim());
    }
    // Try finding the first { to last }
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start !== -1 && end !== -1) {
      return JSON.parse(text.substring(start, end + 1));
    }
    throw new Error('Could not parse AI response as JSON');
  }
};

/**
 * Generate questions using Google Gemini
 */
export const generateWithGemini = async (params, existingPassageText = null) => {
  if (!GEMINI_API_KEY || GEMINI_API_KEY === 'YOUR_GEMINI_API_KEY_HERE') {
    throw new Error('Gemini API key is not configured. Please add your API key to the .env file.');
  }

  const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
  const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

  const prompt = buildPrompt(params, existingPassageText);

  const result = await model.generateContent(prompt);
  const response = await result.response;
  const text = response.text();

  const jsonData = extractJSON(text);
  return jsonData;
};

/**
 * Generate questions using OpenAI ChatGPT
 */
export const generateWithChatGPT = async (params, existingPassageText = null) => {
  if (!OPENAI_API_KEY || OPENAI_API_KEY === 'YOUR_OPENAI_API_KEY_HERE') {
    throw new Error('OpenAI API key is not configured. Please add your API key to the .env file.');
  }

  const openai = new OpenAI({
    apiKey: OPENAI_API_KEY,
    dangerouslyAllowBrowser: true
  });

  const config = getExamConfig(params.examType);
  const prompt = buildPrompt(params, existingPassageText);

  const completion = await openai.chat.completions.create({
    model: 'gpt-4o-mini',
    messages: [
      {
        role: 'system',
        content: config.systemMessage
      },
      {
        role: 'user',
        content: prompt
      }
    ],
    temperature: 0.7,
    response_format: { type: 'json_object' }
  });

  const text = completion.choices[0]?.message?.content;
  if (!text) {
    throw new Error('No response received from ChatGPT');
  }

  const jsonData = extractJSON(text);
  return jsonData;
};

/**
 * Main function: Generate questions using the selected AI model
 */
export const generateQuestions = async (params, existingPassage = null) => {
  const { aiModel } = params;

  // Get existing passage text if applicable
  let existingPassageText = null;
  if (existingPassage && existingPassage.passageText) {
    // Extract plain text from Draft.js raw format
    const blocks = existingPassage.passageText.blocks || [];
    existingPassageText = blocks.map(b => b.text).join('\n');
  }

  let jsonData;
  if (aiModel === 'chatgpt') {
    jsonData = await generateWithChatGPT(params, existingPassageText);
  } else {
    jsonData = await generateWithGemini(params, existingPassageText);
  }

  // Parse AI response into app data structure
  const result = parseAIResponse(jsonData, params, existingPassage);
  return result;
};
